import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_GENDERS_LOAD,
    ACTION_GENDERS_LOAD_BY_ID,
    ACTION_GENDERS_LOAD_FAILURE,
    ACTION_GENDERS_LOAD_SUCCESS,

    ACTION_GENDERS_SAVE,
    ACTION_GENDERS_SAVE_SUCCESS,
    ACTION_GENDERS_SAVE_FAILURE,

    ACTION_GENDERS_CREATE,
    ACTION_GENDERS_CREATE_SUCCESS,
    ACTION_GENDERS_CREATE_FAILURE,

    ACTION_GENDERS_CLEAR,
    ACTION_GENDERS_FILTER,

    GenderAction
} from '../actions/genders';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface IGenderState {
    formData: Dtos.Gender | Dtos.Gender[];
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    formRoles: Dtos.RoleType[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[];
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.Gender[];
}

const initialState: IGenderState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    formRoles: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: []
};


function loading(state: IGenderState, isLoading: boolean): IGenderState {
    const newState: IGenderState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(state: IGenderState,
    formData: Dtos.Gender | Dtos.Gender[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    formRoles: Dtos.RoleType[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IGenderState {
    const newState: IGenderState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        formRoles: {
            $set: formRoles
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function filter(state: IGenderState, formFilter: IFormFilter): IGenderState {

    const genders: Dtos.Gender[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formFilteredData = genders ? genders.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" || p.value.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: IGenderState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        }
    });

    return newState;
}

function loadFailure(state: IGenderState, responseStatus: Dtos.ResponseStatus): IGenderState {
    const newState: IGenderState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function clear(state: IGenderState, propertiesToClear?: string[]): IGenderState {

    if (!propertiesToClear) {
        let formFilter = state.formFilter;
        const newState: IGenderState = update(initialState, {
            formFilter: {
                $set: formFilter
            }
        });
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IGenderState, isSaving: boolean): IGenderState {
    const newState: IGenderState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IGenderState,
    formData: Dtos.Gender | Dtos.Gender[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IGenderState {
    const newState: IGenderState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IGenderState, responseStatus: Dtos.ResponseStatus): IGenderState {
    const newState: IGenderState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IGenderState, isCreating: boolean): IGenderState {
    const newState: IGenderState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IGenderState,
    formData: Dtos.Gender,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    formRoles: Dtos.RoleType[],
    lookups: Dtos.Lookup[]
): IGenderState {
    const newState: IGenderState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        formRoles: {
            $set: formRoles
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IGenderState, responseStatus: Dtos.ResponseStatus): IGenderState {
    const newState: IGenderState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

const GenderReducer: Reducer<IGenderState> = (state: IGenderState = initialState, action: GenderAction) => {
    switch (action.type) {
        case ACTION_GENDERS_LOAD:
        case ACTION_GENDERS_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_GENDERS_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.formRoles, action.lookups, action.responseStatus);
       case ACTION_GENDERS_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_GENDERS_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_GENDERS_SAVE:
            return saving(state, true);
        case ACTION_GENDERS_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_GENDERS_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_GENDERS_CREATE:
            return creating(state, true);
        case ACTION_GENDERS_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.formRoles, action.lookups);
        case ACTION_GENDERS_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_GENDERS_FILTER:
            return filter(state, action.formFilter);

    }

    return state;
}

export default GenderReducer;